import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '../common/Drawer';
import Btn from '../common/Btn';
import { statusLabel, statusTokens, STATUS_KINDS } from './statusConfig';
import { getSourceComponentMeta } from './sourceComponents';
import { timeSinceTS } from '../../helpers/dates';
import { getOperation, getTasksForOp, updateOperation, updateTask } from '../../api/operations';
import { setAlert } from '../../helpers/alerts';
import RunOpsView, { changelogOperationsForEntry } from './branchDrawer/RunOpsView';
import OperationTasksView from './branchDrawer/OperationTasksView';
import TaskDetailView from './branchDrawer/TaskDetailView';
import {
  ICO_BRANCH,
  ICO_CHECK,
  ICO_CLOUD,
  ICO_MOON,
  ICO_REFRESH,
  ICO_ROCKET,
  ICO_XMARK,
  NON_VALIDATION_STEP_KEYS,
} from './branchDrawer/constants';
import {
  conclusionColor,
  displayStatusForNightlyOperation,
  formatDuration,
  getStepIcon,
  isAutNightlyOperation,
  isSkippedStep,
  isVulnerabilityOperation,
  mergeOperationSummary,
  operationConclusion,
  operationResultUrl,
  operationCommitUrl,
} from './branchDrawer/helpers';

const RERUN_STATUSES = ['completed', 'errored'];

function entryLabel(entry) {
  if (entry.kind === 'run') return `Nightly #${entry.data.number}`;
  if (entry.kind === 'op') return entry.name || entry.data?.name || 'Operation';
  if (entry.kind === 'task') return entry.data.task_id;
  return entry.data?.component || 'Op';
}

function unwrapOperationResponse(opRes) {
  return typeof opRes.operation === 'object' ? opRes.operation : opRes;
}

// Free tier can be (re)triggered until a rollout has actually started in collate-cloud
function canStartFreeTier(run) {
  const rollout = run?.free_tier_rollout;
  return !rollout?.status || (rollout.status === 'failed' && !rollout.finished);
}

function hasRollout(run) {
  return (
    run?.rollout_created === true ||
    run?.rollout_created === 'true' ||
    (run?.deployments || []).length > 0
  );
}

export default function BranchDrawer({ drawerStack, onPush, onPop, onPopTo, onClose, onOpenDeploy, onDeployFreeTier, version, releaseVersion, allClusters = [] }) {
  const [opData,     setOpData]     = useState(null);
  const [opLoading,  setOpLoading]  = useState(false);
  const [opError,    setOpError]    = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [taskPatch,  setTaskPatch]  = useState(null);

  const open    = drawerStack.length > 0;
  const current = drawerStack[drawerStack.length - 1];
  const prev    = drawerStack[drawerStack.length - 2];

  const clusterMap = useMemo(() => {
    const map = {};
    allClusters.forEach(c => { map[c.id] = c; });
    return map;
  }, [allClusters]);

  useEffect(() => {
    if (!current || current.kind !== 'op' || isSkippedStep(current.data)) {
      setOpData(null);
      setOpLoading(false);
      setOpError(null);
      return;
    }

    if (current.data?.tasks) {
      setOpData(current.data);
      setOpLoading(false);
      setOpError(null);
      return;
    }

    setOpLoading(true);
    setOpError(null);
    const opId = current.data?.operation_id || current.id;
    Promise.all([getOperation(opId), getTasksForOp(opId)])
      .then(([opRes, tasksRes]) => {
        setOpData({ ...unwrapOperationResponse(opRes), tasks: tasksRes.tasks || [] });
        setOpLoading(false);
      })
      .catch(() => {
        setOpError(true);
        setOpLoading(false);
      });
  }, [current]);

  if (!open || !current) return null;

  const isRun  = current.kind === 'run';
  const isOp   = current.kind === 'op';
  const isTask = current.kind === 'task';
  const isBranchingOp = current.kind === 'branchingOp';
  const taskData = isTask && taskPatch?.uuid === current.data.uuid
    ? { ...current.data, ...taskPatch }
    : current.data;
  const opSource = isOp ? mergeOperationSummary(current.data, opData) : current.data;

  const displayOpStatus = isOp ? displayStatusForNightlyOperation(opSource) : null;
  const statusSource = isTask ? taskData : { ...opSource, status: displayOpStatus || opSource.status };
  const statusTok = statusTokens(statusSource.status) || STATUS_KINDS.neutral;
  const opStepName = isOp ? (current.name || '') : '';
  const opCompMeta = isOp ? getSourceComponentMeta(opStepName) : null;

  const title = isTask
    ? taskData.task_id
    : isOp
      ? (opSource?.name || current.name || 'Operation')
      : isBranchingOp
        ? current.data.component
        : isRun
          ? `Nightly #${current.data.number}`
          : 'Operation';

  const icon = isRun         ? ICO_MOON
             : isBranchingOp ? ICO_BRANCH
             : isOp          ? (opCompMeta ? null : getStepIcon(opStepName))
             : isTask        ? (taskData.status === 'completed' ? ICO_CHECK
                              : taskData.status === 'errored'   ? ICO_XMARK
                              : null)
             : ICO_ROCKET;

  const iconEl = opCompMeta
    ? opCompMeta.icon(13)
    : (isTask && !['completed', 'errored'].includes(taskData.status))
      ? <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'currentColor', display: 'block' }} />
      : null;

  const iconStyle = isRun
    ? { background: 'var(--brand-tint)', color: 'var(--brand-primary-hover)' }
    : (isOp || isTask)
      ? (opCompMeta
          ? { background: opCompMeta.bg }
          : { background: statusTok.bg, color: statusTok.fg })
      : undefined;

  const subtitle = isRun
    ? `branch ${version} · triggered ${timeSinceTS(current.data.triggered_at)}`
    : isOp
      ? (prev?.kind === 'run'
          ? `Nightly #${prev.data.number} · ${opSource?.stepName || current.name || 'operation'} · branch ${version}`
          : opSource?.component
            ? (opSource.stage === 'post_release' || opSource.metadata?.stage === 'post_release'
                ? `Version-bump commit · branch ${version}`
                : opSource.stage === 'release' || opSource.metadata?.stage === 'release'
                  ? `${opSource.stepName || 'operation'} · release ${releaseVersion || version}`
                  : `Branching operation · branch ${version}`)
            : undefined)
      : isTask
        ? `Task · ${opData?.name || prev?.data?.name || prev?.name || ''}`
        : undefined;

  const stats = isTask ? []
    : isRun ? (() => {
        const ops   = Object.entries(current.data.result || {})
          .filter(([key]) => !NON_VALIDATION_STEP_KEYS.includes(key))
          .map(([, op]) => op);
        const okCnt = ops.filter(o => o.status === 'completed').length;
        const img   = current.data.build_tag || current.data.result?.build_docker?.image_tag;
        const [changelogStep] = changelogOperationsForEntry('run', current.data);
        const changelogStatus = changelogStep && displayStatusForNightlyOperation(changelogStep);
        return [
          { label: 'Status',  value: statusLabel(current.data.status), color: statusTok.fg },
          ...(img ? [{ label: 'Image', value: img, mono: true }] : []),
          { label: 'Trigger', value: current.data.triggered_by ? 'Manual' : 'Nightly (auto)' },
          ...(ops.length ? [{ label: 'Steps', value: `${okCnt}/${ops.length}`, color: okCnt === ops.length ? 'var(--success-700)' : 'var(--error-700)' }] : []),
          ...(changelogStep ? [{
            label: 'Changelog',
            value: statusLabel(changelogStatus),
            color: (statusTokens(changelogStatus) || STATUS_KINDS.neutral).fg,
            onClick: () => handleOpenOp({ name: changelogStep.name, op: changelogStep }),
          }] : []),
        ];
      })()
    : isOp ? (() => {
        const opTasks = opData?.tasks || [];
        const doneCnt = opTasks.filter(t => t.status === 'completed').length;
        const hasErr  = opTasks.some(t => t.status === 'errored');
        const url = operationResultUrl(opSource);
        const commitUrl = operationCommitUrl(opSource);
        const conclusion = operationConclusion(opSource);
        const showConclusion = conclusion && !isAutNightlyOperation(opSource) && !isVulnerabilityOperation(opSource);
        return [
          { label: 'Status', value: statusLabel(displayOpStatus || opSource.status), color: statusTok.fg },
          ...(showConclusion ? [{ label: 'Conclusion', value: conclusion, color: conclusionColor(conclusion) }] : []),
          ...(url ? [{
            label: 'Run',
            value: opSource?.operation === 'vulnerability_scan' ? 'Inspector Findings' : 'Workflow run',
            href: url,
          }] : []),
          ...(commitUrl ? [{ label: 'Commit', value: 'View commit', href: commitUrl }] : []),
          ...(opTasks.length ? [{ label: 'Tasks', value: `${doneCnt}/${opTasks.length}`, color: hasErr ? 'var(--error-700)' : 'var(--fg-primary)' }] : []),
          { label: 'Duration', value: formatDuration(opData?.start_date, opData?.end_date), mono: true },
        ];
      })()
    : [
        { label: 'Status', value: statusLabel(current.data.status) },
        { label: 'Triggered', value: timeSinceTS(current.data.triggered_at || current.data.created) },
      ];

  const breadcrumbs = drawerStack.slice(0, -1).map((entry, i) => ({
    label: entryLabel(entry),
    onClick: () => onPopTo?.(i),
  }));

  const handleOpenOp = ({ name, op }) => onPush({ kind: 'op', id: op.operation_id || name, name, data: op });
  const handleOpenTask = (task) => {
    setTaskPatch(null);
    onPush({ kind: 'task', id: task.uuid, data: task });
  };

  const refetchCurrentOperation = () => {
    if (!isOp) return Promise.resolve();
    const opId = current.data?.operation_id || current.id;
    return Promise.all([getOperation(opId), getTasksForOp(opId)])
      .then(([opRes, tasksRes]) => {
        setOpData({ ...unwrapOperationResponse(opRes), tasks: tasksRes.tasks || [] });
      });
  };

  const handleRerunOperation = () => {
    const opId = opData?.uuid || current.data?.operation_id || current.id;
    setActionBusy('rerun-op');
    updateOperation(opId, 'rerun')
      .then(() => refetchCurrentOperation())
      .then(() => setAlert('Operation', 'Operation rerun requested', 'success'))
      .catch((e) => setAlert('Operation rerun error', e, 'danger'))
      .finally(() => setActionBusy(null));
  };

  const handleRerunTask = () => {
    setActionBusy('rerun-task');
    updateTask(taskData.uuid, 'rerun')
      .then((updated) => {
        setTaskPatch(updated);
        setAlert('Task', 'Task rerun requested', 'success');
      })
      .catch((e) => setAlert('Task rerun error', e, 'danger'))
      .finally(() => setActionBusy(null));
  };

  const canRerunOp = isOp && !isSkippedStep(current.data) && RERUN_STATUSES.includes(opSource.status);
  const canRerunTask = isTask && RERUN_STATUSES.includes(taskData.status);
  const canDeployRun = isRun
    && current.data.result?.build_docker?.status === 'completed'
    && current.data.build_tag
    && current.data.rollout_status !== 'replicating';
  const deployLabel = hasRollout(current.data) ? 'Add clusters' : 'Deploy';

  const canDeployFreeTier = canDeployRun && canStartFreeTier(current.data);

  const footerActions = canDeployRun ? (
    <>
      <Btn
        variant="primary"
        size="md"
        icon={ICO_ROCKET}
        onClick={() => onOpenDeploy?.(current.data)}
      >
        {deployLabel}
      </Btn>
      {canDeployFreeTier && (
        <Btn
          variant="secondary"
          size="md"
          icon={ICO_CLOUD}
          onClick={() => onDeployFreeTier?.(current.data)}
        >
          {current.data.free_tier_rollout?.status === 'failed' ? 'Retry free tier' : 'Deploy to free tier'}
        </Btn>
      )}
    </>
  ) : canRerunOp ? (
    <Btn
      variant="secondary"
      size="md"
      icon={ICO_REFRESH}
      loading={actionBusy === 'rerun-op'}
      onClick={handleRerunOperation}
    >
      Rerun
    </Btn>
  ) : canRerunTask ? (
    <Btn
      variant="secondary"
      size="md"
      icon={ICO_REFRESH}
      loading={actionBusy === 'rerun-task'}
      onClick={handleRerunTask}
    >
      Rerun
    </Btn>
  ) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      breadcrumbs={breadcrumbs}
      backLabel={drawerStack.length > 1 ? 'Back' : 'Close'}
      onBack={drawerStack.length > 1 ? onPop : onClose}
      icon={icon}
      iconEl={iconEl}
      iconStyle={iconStyle}
      title={title}
      subtitle={subtitle}
      titleMono={isTask || isRun || isOp}
      status={statusSource.status}
      stats={stats}
      footer={footerActions}
      frameKey={current.id}
    >
      {(isRun || isBranchingOp) && (
        <RunOpsView entry={current} onOpenOp={handleOpenOp} clusterMap={clusterMap} />
      )}
      {isOp && (
        <OperationTasksView
          opData={opData}
          opLoading={opLoading}
          opError={opError}
          opSummary={opSource}
          onOpenTask={handleOpenTask}
          onAssigned={refetchCurrentOperation}
        />
      )}
      {isTask && <TaskDetailView task={taskData} />}
    </Drawer>
  );
}
